import { useEffect, useRef, useState } from 'react';
import {
  Alert,
  Button,
  Group,
  Select,
  Stack,
  Text,
  Textarea,
  TextInput,
  Title,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  IconAlertCircle,
  IconArrowLeft,
  IconCheck,
  IconDownload,
  IconPrinter,
  IconTrash,
} from '@tabler/icons-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { formatMoney, type MeasureRoom } from '@erp/shared';
import { ApiError } from '../api/client';
import { estimatesApi, type EstimateCreateInput, type EstimateUpdateInput } from '../api/estimates';
import { unitsOfMeasureApi, vatRatesApi } from '../api/catalogs';
import { counterpartiesApi } from '../api/counterparties';
import { priceListItemsApi } from '../api/priceList';
import { organizationsApi } from '../api/organizations';
import {
  EstimateLinesEditor,
  effectiveQuantity,
  parseDiscountPercent,
  emptyEstimateLineRow,
  type EstimateLineRow,
} from '../components/EstimateLinesEditor';
import { MeasurementsEditor } from '../components/MeasurementsEditor';
import { useAuth } from '../auth/AuthContext';

function today(): string {
  return new Date().toISOString().slice(0, 10);
}

function toDateInput(value: string | null): string {
  return value ? value.slice(0, 10) : '';
}

export function EstimateFormPage() {
  const { id } = useParams<{ id: string }>();
  const isNew = id === 'new' || !id;
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { hasRole } = useAuth();
  const canEditGlobally = hasRole('ADMIN', 'ACCOUNTANT', 'MANAGER');

  const [organizationId, setOrganizationId] = useState('');
  const [counterpartyId, setCounterpartyId] = useState('');
  const [date, setDate] = useState(today());
  const [validUntil, setValidUntil] = useState('');
  const [objectName, setObjectName] = useState('');
  const [objectAddress, setObjectAddress] = useState('');
  const [comment, setComment] = useState('');
  const [footerText, setFooterText] = useState('');
  const [discountPercent, setDiscountPercent] = useState('');
  const [lines, setLines] = useState<EstimateLineRow[]>([emptyEstimateLineRow()]);
  const [rooms, setRooms] = useState<MeasureRoom[]>([]);

  // Удалённое помещение убираем и из строк сметы: иначе строка ссылалась бы
  // на несуществующее помещение, и сервер отклонил бы сохранение.
  function changeRooms(next: MeasureRoom[]) {
    const ids = new Set(next.map((r) => r.id));
    setRooms(next);
    setLines((prev) =>
      prev.map((l) =>
        l.measureRoomIds.every((rid) => ids.has(rid))
          ? l
          : { ...l, measureRoomIds: l.measureRoomIds.filter((rid) => ids.has(rid)) },
      ),
    );
  }
  const [formError, setFormError] = useState<string[] | null>(null);

  const loadedFor = useRef<string | null>(null);

  const estimateQuery = useQuery({
    queryKey: ['estimate', id],
    queryFn: () => estimatesApi.get(id!),
    enabled: !isNew,
  });

  const organizations = useQuery({
    queryKey: ['organizations', 'all'],
    queryFn: () => organizationsApi.list({ pageSize: 200 }),
  });
  const counterparties = useQuery({
    queryKey: ['counterparties', 'all'],
    queryFn: () => counterpartiesApi.list({ pageSize: 200 }),
  });
  const units = useQuery({ queryKey: ['units-of-measure'], queryFn: () => unitsOfMeasureApi.list() });
  const vatRates = useQuery({ queryKey: ['vat-rates'], queryFn: () => vatRatesApi.list() });
  const priceListItems = useQuery({
    queryKey: ['price-list-items', 'all'],
    queryFn: () => priceListItemsApi.listAll(),
  });

  const estimate = estimateQuery.data?.estimate ?? null;
  const canEditDocument = canEditGlobally;

  // Заполняем форму данными документа один раз при загрузке — иначе фоновый
  // рефетч перезаписывал бы то, что пользователь уже успел набрать.
  useEffect(() => {
    if (!estimate || loadedFor.current === estimate.id) return;
    loadedFor.current = estimate.id;

    setOrganizationId(estimate.organizationId);
    setCounterpartyId(estimate.counterpartyId);
    setDate(toDateInput(estimate.date));
    setValidUntil(toDateInput(estimate.validUntil));
    setObjectName(estimate.objectName ?? '');
    setObjectAddress(estimate.objectAddress ?? '');
    setComment(estimate.comment ?? '');
    setFooterText(estimate.footerText ?? '');
    // Decimal из базы приходит как «10.00» — показываем как ввёл бы человек: «10».
    setDiscountPercent(estimate.discountPercent ? String(Number(estimate.discountPercent)).replace('.', ',') : '');
    setRooms(estimate.measurements ?? []);
    setLines(
      estimate.lines.map((l) => ({
        key: l.id,
        priceListItemId: l.priceListItemId,
        sectionId: l.sectionId,
        sectionName: l.sectionName ?? '',
        name: l.name,
        code: l.code,
        unitId: l.unitId ?? '',
        quantity: l.quantity,
        measureMetric: l.measureMetric,
        measureRoomIds: l.measureRoomIds,
        wastePercent: l.wastePercent ?? '',
        price: l.price,
        vatRateId: vatRates.data?.items.find((r) => r.code === l.vatRateCode)?.id ?? '',
      })),
    );
  }, [estimate, vatRates.data]);

  const organizationOptions = (organizations.data?.items ?? []).map((o) => ({ value: o.id, label: o.shortName }));
  const counterpartyOptions = (counterparties.data?.items ?? []).map((c) => ({ value: c.id, label: c.shortName }));

  const saveMutation = useMutation({
    mutationFn: async () => {
      const body = {
        date,
        validUntil: validUntil || null,
        objectName: objectName || null,
        objectAddress: objectAddress || null,
        comment: comment || null,
        footerText: footerText || null,
        discountPercent: discountPercent.trim() || null,
        measurements: rooms.length > 0 ? rooms : null,
        lines: lines.map((l) => ({
          priceListItemId: l.priceListItemId,
          sectionId: l.sectionId,
          sectionName: l.sectionName || null,
          name: l.name.trim(),
          code: l.code || null,
          unitId: l.unitId || null,
          quantity: effectiveQuantity(l, rooms),
          measureMetric: l.measureMetric,
          measureRoomIds: l.measureRoomIds,
          wastePercent: l.wastePercent || null,
          price: l.price,
          vatRateId: l.vatRateId,
        })),
      };
      if (isNew) {
        const payload: EstimateCreateInput = { ...body, organizationId, counterpartyId };
        return estimatesApi.create(payload);
      }
      return estimatesApi.update(id!, body satisfies EstimateUpdateInput);
    },
    onSuccess: (data) => {
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
      void queryClient.invalidateQueries({ queryKey: ['estimate', data.estimate.id] });
      notifications.show({ color: 'green', message: 'Смета сохранена' });
      if (isNew) navigate(`/estimates/${data.estimate.id}`, { replace: true });
    },
    onError: (err) => {
      setFormError(err instanceof ApiError ? (err.details ?? [err.message]) : ['Не удалось сохранить']);
    },
  });

  function handleSubmit() {
    setFormError(null);
    const errors: string[] = [];
    if (isNew && !organizationId) errors.push('Укажите организацию');
    if (isNew && !counterpartyId) errors.push('Укажите заказчика');
    if (!date) errors.push('Укажите дату');
    if (validUntil && validUntil < date) errors.push('Срок действия не может быть раньше даты сметы');
    if (parseDiscountPercent(discountPercent) === undefined) errors.push('Скидка — число от 0 до 100');
    if (lines.length === 0) errors.push('Добавьте хотя бы одну строку');
    lines.forEach((l, i) => {
      if (!l.name.trim()) errors.push(`Строка ${i + 1}: укажите наименование`);
      if (!l.vatRateId) errors.push(`Строка ${i + 1}: укажите ставку НДС`);
      if (l.measureMetric) {
        if (Number(effectiveQuantity(l, rooms)) <= 0) {
          errors.push(`Строка ${i + 1}: по обмерам объём равен нулю — заполните размеры помещений`);
        }
      } else if (!l.quantity.trim()) {
        errors.push(`Строка ${i + 1}: укажите объём`);
      }
      if (!l.price.trim()) errors.push(`Строка ${i + 1}: укажите цену`);
    });
    if (errors.length > 0) {
      setFormError(errors);
      return;
    }
    saveMutation.mutate();
  }

  const removeMutation = useMutation({
    mutationFn: () => estimatesApi.remove(id!),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['estimates'] });
      notifications.show({ color: 'green', message: 'Смета удалена' });
      navigate('/estimates');
    },
    onError: (err) => {
      notifications.show({
        color: 'red',
        message: err instanceof ApiError ? err.message : 'Не удалось удалить смету',
      });
    },
  });

  function handleRemove() {
    if (!estimate) return;
    if (!window.confirm(`Удалить смету № ${estimate.number}? Действие необратимо.`)) return;
    removeMutation.mutate();
  }

  if (!isNew && !estimate) {
    return <Text c="dimmed">Загрузка…</Text>;
  }

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Group gap="sm">
          <Button variant="subtle" leftSection={<IconArrowLeft size={16} />} onClick={() => navigate('/estimates')}>
            К списку
          </Button>
          <Title order={2}>{isNew ? 'Новая смета' : `Смета № ${estimate?.number ?? ''}`}</Title>
        </Group>

        {estimate && (
          <Group gap="xs">
            <Button
              variant="default"
              leftSection={<IconPrinter size={16} />}
              component="a"
              href={`/estimates/${estimate.id}/print`}
              target="_blank"
            >
              Печать
            </Button>
            <Button
              variant="default"
              leftSection={<IconDownload size={16} />}
              onClick={() => window.open(`/estimates/${estimate.id}/print?download=1`, '_blank')}
            >
              Скачать PDF
            </Button>
            {canEditDocument && (
              <Button
                color="red"
                variant="light"
                leftSection={<IconTrash size={16} />}
                onClick={handleRemove}
                loading={removeMutation.isPending}
              >
                Удалить
              </Button>
            )}
          </Group>
        )}
      </Group>

      {estimate && (
        <Text size="sm" c="dimmed">
          {estimate.organization.shortName} → {estimate.counterparty.shortName}
        </Text>
      )}

      {formError && (
        <Alert icon={<IconAlertCircle size={16} />} color="red" variant="light">
          <Stack gap={2}>
            {formError.map((e, i) => (
              <Text key={i} size="sm">
                {e}
              </Text>
            ))}
          </Stack>
        </Alert>
      )}

      {/* Организация и заказчик выбираются только при создании: у существующей
          сметы смена организации сломала бы уже выданный номер, а смена
          заказчика превратила бы документ в другой. */}
      {isNew && (
        <Group grow align="flex-start">
          <Select
            label="Организация (Исполнитель)"
            placeholder="Выберите организацию"
            data={organizationOptions}
            value={organizationId || null}
            onChange={(v) => setOrganizationId(v ?? '')}
            searchable
            required
          />
          <Select
            label="Заказчик"
            placeholder="Выберите контрагента"
            data={counterpartyOptions}
            value={counterpartyId || null}
            onChange={(v) => setCounterpartyId(v ?? '')}
            searchable
            required
          />
        </Group>
      )}

      <Group grow align="flex-start">
        <TextInput
          label="Дата сметы"
          type="date"
          value={date}
          onChange={(e) => setDate(e.currentTarget.value)}
          disabled={!canEditDocument}
          required
        />
        <TextInput
          label="Срок действия КП"
          type="date"
          value={validUntil}
          onChange={(e) => setValidUntil(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <Group grow align="flex-start">
        <TextInput
          label="Объект"
          placeholder="Название объекта"
          value={objectName}
          onChange={(e) => setObjectName(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
        <TextInput
          label="Адрес объекта"
          value={objectAddress}
          onChange={(e) => setObjectAddress(e.currentTarget.value)}
          disabled={!canEditDocument}
        />
      </Group>

      <MeasurementsEditor
        key={estimate?.id ?? 'new'}
        estimateId={estimate?.id}
        rooms={rooms}
        onChange={changeRooms}
        canEdit={canEditDocument}
      />

      <EstimateLinesEditor
        lines={lines}
        onChange={setLines}
        rooms={rooms}
        canEdit={canEditDocument}
        priceListItemOptions={priceListItems.data ?? []}
        units={units.data?.items ?? []}
        vatRates={vatRates.data?.items ?? []}
        discountPercent={discountPercent}
        onDiscountPercentChange={setDiscountPercent}
      />

      {estimate && (
        <Text size="sm" c="dimmed">
          Всего: {formatMoney(estimate.total)}
        </Text>
      )}

      <Textarea
        label="Текст под таблицей"
        value={footerText}
        onChange={(e) => setFooterText(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={2}
      />

      <Textarea
        label="Комментарий"
        value={comment}
        onChange={(e) => setComment(e.currentTarget.value)}
        disabled={!canEditDocument}
        autosize
        minRows={1}
      />

      {canEditDocument && (
        <Group>
          <Button leftSection={<IconCheck size={16} />} onClick={handleSubmit} loading={saveMutation.isPending}>
            Сохранить
          </Button>
        </Group>
      )}
    </Stack>
  );
}
